import { Checkbox, CheckboxSize, Show } from "shared/components";
import { DataGridTable } from "shared/components/DataGridTable";
import { Column, SELECT_COLUMN_KEY, SortColumn } from "react-data-grid";

import { useLocation, useNavigate } from "react-router-dom";
import {
  ApplicantStatus,
  ApplicationFeeStatus,
  ApplicationStatus
} from "shared/types";
import {
  Dispatch,
  lazy,
  ReactNode,
  SetStateAction,
  Suspense,
  useMemo,
  useState
} from "react";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Applicant, Application } from "types/application";

import { useAuthContext } from "providers/Auth";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ScreeningStatusBadge } from "../ScreeningStatusBadge";
import { ActiveTab, newOrder } from "./constants";
import {
  formatCombinedNamesAndEmails,
  getApplicantNames,
  getInviteesEmails
} from "./helpers";
import { useLayout } from "providers/Layout/hooks";
import {
  CashbackIcon,
  FileEmpty02Icon,
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon,
  Search01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  getApplicationSplittingDisabled,
  getApplicationSplittingTooltip
} from "../../utils/applicationSplittingUtils";
import { DateTimeTableCell } from "shared/components/DateTimeTableCell";
import {
  camelToSentence,
  capitalize,
  underscoreToSentence
} from "shared/utils";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Archive, ArrowRight } from "untitledui-js-base";

const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);

type ApplicationWithUIStatus = Application & {
  ui_status: ApplicationStatus;
  isGroupHeader?: boolean;
  groupValue?: string;
};

const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

export const ApplicationsDataGridTable = ({
  groupBy,
  applications,
  onInviteClick,
  activeTab,
  hasSearchParams,
  isLoading,
  setSelectedRentersRow,
  selectedRentersRow,
  onCancelClick,
  onUncancelClick,
  onMoveClick,
  onRefundClick,
  onSplitClick,
  sorting,
  setSorting,
  sortableKeys,
  columnsVisibility,
  invalidateQueries,
  pagination,
  perPageRows
}: {
  groupBy?: string;
  applications: Application[];
  onInviteClick?: () => void;
  activeTab: ActiveTab;
  isPending?: boolean;
  isCanceled?: boolean;
  hasSearchParams?: boolean;
  setSelectedRentersRow: Dispatch<SetStateAction<Record<string, boolean>>>;
  selectedRentersRow: Record<string, boolean>;
  onCancelClick?: (id: string) => void;
  onUncancelClick?: (id: string) => void;
  onMoveClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  onSplitClick?: (id: string) => void;
  isLoading?: boolean;
  sorting: { id: string; desc: boolean }[];
  setSorting: Dispatch<SetStateAction<{ id: string; desc: boolean }[]>>;
  sortableKeys: string[];
  invalidateQueries?: unknown[][];
  columnsVisibility: { id: string; checked: boolean; disabled: boolean }[];
  pagination?: React.ReactNode;
  perPageRows: number;
}) => {
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const { pathname } = useLocation();
  const { type, permissions } = useAuthContext();
  const { adminPrivileges } = type;
  const { sectionHeight } = useLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedRows, setSelectedRows] = useState<Set<string>>(
    new Set(
      Object.keys(selectedRentersRow).filter((k) => selectedRentersRow[k])
    )
  );

  // Convert sorting state to react-data-grid format
  const sortColumns: SortColumn[] = useMemo(() => {
    return sorting.map((s) => ({
      columnKey: s.id,
      direction: s.desc ? ("DESC" as const) : ("ASC" as const)
    }));
  }, [sorting]);

  const handleSortColumnsChange = (newSortColumns: SortColumn[]) => {
    // Only keep the last sort (single column sorting)
    const lastSort = newSortColumns[newSortColumns.length - 1];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- index search doesn't guarantee lastSort will not be undefined
    if (lastSort)
      setSorting([
        {
          id: lastSort.columnKey,
          desc: lastSort.direction === "DESC"
        }
      ]);
    else setSorting([]);
  };

  const tableApplications = useMemo<ApplicationWithUIStatus[]>(() => {
    const appsWithStatus = applications.map((application) => ({
      ...application,
      ui_status: getApplicationUIStatus(
        application as unknown as ApplicationLike
      )
    }));

    // Add group headers if groupBy is set
    if (!groupBy || groupBy === "") return appsWithStatus;

    const groupByMap: Record<string, string> = {
      unit: "unit.title",
      status: "ui_status",
      screening_status: "screening_status"
    };

    const groupByKey = groupByMap[groupBy] || groupBy;
    const grouped: Record<string, ApplicationWithUIStatus[]> = {};

    appsWithStatus.forEach((app) => {
      const groupValue = String(getNestedValue(app, groupByKey) || "");
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- namesearch doesn't guarantee groupValue will not be undefined
      if (!grouped[groupValue]) grouped[groupValue] = [];
      grouped[groupValue].push(app);
    });

    const result: ApplicationWithUIStatus[] = [];
    Object.entries(grouped).forEach(([groupValue, apps]) => {
      // Add group header row
      result.push({
        ...apps[0],
        id: `group-${groupValue}` as any,
        isGroupHeader: true,
        groupValue
      });
      // Add data rows
      result.push(...apps);
    });

    return result;
  }, [applications, groupBy]);

  const currentTab = {
    isAll: activeTab === ActiveTab.All,
    isNotSubmitted: activeTab === ActiveTab.NotSubmitted,
    isPartiallySubmitted: activeTab === ActiveTab.PartiallySubmitted,
    isSubmitted: activeTab === ActiveTab.Submitted,
    isApproved: activeTab === ActiveTab.Approved,
    isConditionallyApproved: activeTab === ActiveTab.ApprovedConditionally,
    isDeclined: activeTab === ActiveTab.Declined,
    isCanceled: activeTab === ActiveTab.Canceled,
    isOnHold: activeTab === ActiveTab.OnHold
  };

  const sortedColumnsVisibility = columnsVisibility.toSorted((a, b) => {
    const indexA = newOrder.indexOf(a.id);
    const indexB = newOrder.indexOf(b.id);
    return indexA - indexB;
  });

  const hasMultipleCheckedColumns =
    sortedColumnsVisibility.filter((col) => col.checked).length > 1;

  const onResumeHoldSideEffect = () => {
    invalidateQueries?.forEach((queryKey) => {
      queryClient.refetchQueries({
        queryKey
      });
    });
  };

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);

      // Update parent state
      const newState: Record<string, boolean> = {};
      newSet.forEach((rowId) => {
        newState[rowId] = true;
      });
      setSelectedRentersRow(newState);

      return newSet;
    });
  };

  const columns = useMemo<Column<ApplicationWithUIStatus>[]>(() => {
    const cols: Column<ApplicationWithUIStatus>[] = [];

    // Checkbox column
    cols.push({
      key: SELECT_COLUMN_KEY,
      name: "",
      width: 52,
      minWidth: 52,
      frozen: true,
      resizable: false,
      renderCell: ({ row }) => {
        if (row.isGroupHeader) return null;

        return (
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip placement="top-end">
              <TooltipTrigger>
                <Checkbox
                  wrapperClassName="!w-auto !flex-none !p-3 justify-center hover:bg-alpha-40 rounded-full"
                  size={CheckboxSize.SM}
                  checked={selectedRows.has(String(row.id))}
                  disabled={row.status === ApplicationStatus.OnHold}
                  onChange={() => toggleRowSelection(String(row.id))}
                />
                <Show if={row.status === ApplicationStatus.OnHold}>
                  <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white">
                    Resume this application before selecting it for merging
                  </TooltipContent>
                </Show>
              </TooltipTrigger>
            </Tooltip>
          </div>
        );
      }
    });

    // Dynamic columns based on visibility
    sortedColumnsVisibility.forEach((col) => {
      if (!col.checked || col.disabled) return;

      if (col.id === "criteria")
        cols.push({
          key: "applicant_score",
          name: "Criteria",
          width: 103,
          minWidth: 103,
          resizable: true,
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            const templateCriteriaSettings =
              row.verification_template?.screening_criteria;

            const hasValue =
              [
                ApplicationStatus.Submitted,
                ApplicationStatus.Approved,
                ApplicationStatus.Rejected,
                ApplicationStatus.ConditionalApproved,
                ApplicationStatus.Canceled
              ].includes(row.status) || row.submitted_at;

            if (
              !row.verification_template?.recommendation_method ||
              row.verification_template.recommendation_method === "none"
            )
              return null;

            if (
              row.verification_template.recommendation_method === "score_based"
            )
              return (
                <div className="overflow-hidden pl-2">
                  <Suspense fallback={<Loader />}>
                    <ScoreGraph
                      size={ScoreGraphSize.XS}
                      score={hasValue ? row.score : null}
                      declineRangeEnd={
                        templateCriteriaSettings?.decline_range_end
                      }
                      conditionalApprovalRangeEnd={
                        templateCriteriaSettings?.conditional_approval_range_end
                      }
                    />
                  </Suspense>
                </div>
              );

            return (
              <div className="overflow-hidden pl-6">
                <RuleBaseGraph
                  recommendation={hasValue ? row.recommendation : null}
                />
              </div>
            );
          }
        });

      if (col.id === "applicants")
        cols.push({
          key: "applicants",
          name: "Applicants",
          width: 282,
          minWidth: 180,
          frozen: true,
          resizable: true,
          // eslint-disable-next-line sonarjs/function-return-type -- null is expected return type.
          renderCell: ({ row }) => {
            if (row.isGroupHeader)
              // Render group header
              return renderGroupHeader(row, groupBy);

            const { applicants, pending_magic_links } = row;

            const total_applicants =
              (applicants.length || 0) + (pending_magic_links?.length || 0);

            const applicantNames = getApplicantNames(applicants as any);
            const inviteesEmails = getInviteesEmails(pending_magic_links || []);

            const numApplicantsPrefix =
              total_applicants > 1 ? `${total_applicants} ` : "";
            const ret = `${numApplicantsPrefix} ${formatCombinedNamesAndEmails(applicantNames, inviteesEmails)}`;

            return ret as ReactNode;
          }
        });

      if (col.id === "property" && !pathname.includes("properties"))
        cols.push({
          key: "property.address.address1",
          name: Property,
          width: 282,
          minWidth: 180,
          resizable: true,
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <span className="block 2xl:max-w-none">
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                  {row.property.name}
                </span>
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-400">
                  {row.property.address.address1}
                </span>
              </span>
            );
          }
        });

      if (col.id === "property_group")
        cols.push({
          key: "property.property_group.name",
          name: `${Property} group`,
          width: 140,
          minWidth: 100,
          resizable: true,
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                {row.property.property_group?.name ?? "--"}
              </span>
            );
          }
        });

      if (col.id === "unit")
        cols.push({
          key: "unit.title",
          name: Unit,
          width: 98,
          minWidth: 98,
          resizable: true,
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                {row.unit.title}
              </span>
            );
          }
        });

      if (col.id === "submitted_at")
        cols.push({
          key: "submitted_at",
          name: "Submitted",
          width: 110,
          minWidth: 110,
          resizable: true,
          sortable: sortableKeys.includes("submitted_at"),
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.submitted_at}
              />
            );
          }
        });

      if (col.id === "started_at")
        cols.push({
          key: "started_at",
          name: "Started",
          width: 110,
          minWidth: 110,
          resizable: true,
          sortable: sortableKeys.includes("started_at"),
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.started_at}
              />
            );
          }
        });

      if (col.id === "updated_at")
        cols.push({
          key: "updated_at",
          name: "Updated at",
          width: 135,
          minWidth: 135,
          resizable: true,
          sortable: sortableKeys.includes("updated_at"),
          renderCell: ({ row }) => {
            if (row.isGroupHeader) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.updated_at}
              />
            );
          }
        });
    });

    // Status column (if visible)
    if (
      sortedColumnsVisibility.find((col) => col.id === "status" && col.checked)
    )
      cols.push({
        key: "status",
        name: "Application status",
        width: 130,
        minWidth: 130,
        resizable: true,
        sortable: sortableKeys.includes("status"),
        renderCell: ({ row }) => {
          if (row.isGroupHeader) return null;

          const { conditions, status, auto_approved, ui_status, applicants } =
            row;
          const applicationStatus = ui_status;

          const isAutoCanceled =
            status === ApplicationStatus.Canceled &&
            applicants.every(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Canceled &&
                applicant.canceled_by_auto_cancel
            ) &&
            !applicants.some(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Submitted
            );

          return (
            <div className="flex w-full">
              <ApplicationStatusBadge
                status={applicationStatus}
                conditions={conditions}
                type="application"
                isAutoCanceled={isAutoCanceled}
                autoApproved={auto_approved}
              />
            </div>
          );
        }
      });

    // Actions column (frozen, placed before screening status)
    cols.push({
      key: "actions",
      name: "",
      width: 60,
      minWidth: 60,
      frozen: true,
      resizable: false,
      // eslint-disable-next-line sonarjs/cognitive-complexity
      renderCell: ({ row }) => {
        if (row.isGroupHeader) return null;

        const id = String(row.id);
        const status = row.status as ApplicationStatus;
        const actions: {
          title: string | JSX.Element;
          onClick: () => void;
          disabled?: boolean;
          tooltip?: string;
        }[] = [
          {
            title: "Review",
            onClick: () => {
              navigate(`/applications/${id}`);
            }
          }
        ];

        if (
          ![
            ApplicationStatus.ConditionalApproved,
            ApplicationStatus.Approved,
            ApplicationStatus.Rejected,
            ApplicantStatus.Canceled
          ].includes(status)
        )
          if (status === ApplicationStatus.OnHold)
            actions.push({
              title: (
                <span className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={PlayIcon}
                    size={20}
                    className="text-content-200 shrink-0"
                  />
                  Resume
                </span>
              ),
              onClick: () => {
                resumeApplication({
                  applicationId: id,
                  onSuccess: onResumeHoldSideEffect
                });
              }
            });
          else {
            const disabled =
              status !== ApplicationStatus.Submitted || selectedRows.has(id);

            let tooltip: string | undefined = undefined;
            if (status !== ApplicationStatus.Submitted)
              tooltip = "Only submitted applications can be put on hold";
            if (selectedRows.has(id))
              tooltip = "Cannot place on hold — application selected for merge";

            actions.push({
              title: (
                <span className="flex gap-2 items-center">
                  <HugeiconsIcon
                    icon={PauseIcon}
                    size={20}
                    className="text-content-200 shrink-0"
                  />
                  Place on hold
                </span>
              ),
              disabled,
              tooltip,
              onClick: () => {
                placeOnHoldApplication({
                  applicationId: id,
                  onSuccess: onResumeHoldSideEffect
                });
              }
            });
          }

        if (adminPrivileges || permissions.canChangeUnit)
          actions.push({
            title: (
              <span className="flex gap-2 items-center">
                <ArrowRight size="18" className="stroke-content-200" />
                Move to another {unit}
              </span>
            ),
            tooltip: `You can't move application to another ${unit} once the decision have been made`,
            disabled: [
              ApplicationStatus.Canceled,
              ApplicationStatus.Approved,
              ApplicationStatus.Rejected,
              ApplicationStatus.ConditionalApproved
            ].includes(row.status),
            onClick: () => {
              if (
                ![
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(row.status)
              )
                onMoveClick && onMoveClick(id);
            }
          });

        actions.push({
          title: (
            <span className="flex gap-2 items-center">
              <HugeiconsIcon
                icon={PathfinderDivideIcon}
                size={18}
                className="text-content-200 shrink-0"
              />
              Split application
            </span>
          ),
          disabled: getApplicationSplittingDisabled(row),
          tooltip: getApplicationSplittingTooltip(row),
          onClick: () => onSplitClick && onSplitClick(id)
        });

        if (
          ApplicationStatus.Canceled === status &&
          (adminPrivileges || permissions.canCancelApplications)
        )
          actions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Uncancel application
              </span>
            ),
            disabled: row.status === ApplicationStatus.OnHold,
            tooltip: "You need to resume the application before uncanceling",
            onClick: () => {
              onUncancelClick && onUncancelClick(id);
            }
          });

        if (
          ApplicationStatus.Canceled !== status &&
          (adminPrivileges || permissions.canCancelApplications)
        )
          actions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Cancel application
              </span>
            ),
            tooltip:
              row.status === ApplicationStatus.Canceled
                ? "Can not cancel a canceled application"
                : "You can't cancel application once the decisions have been made",
            disabled: [
              ApplicationStatus.Canceled,
              ApplicationStatus.Approved,
              ApplicationStatus.Rejected,
              ApplicationStatus.ConditionalApproved
            ].includes(row.status),
            onClick: () => {
              if (
                ![
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(row.status)
              )
                onCancelClick && onCancelClick(id);
            }
          });

        if (adminPrivileges) {
          const { applicants } = row;

          let tooltip: string | undefined = undefined;

          if (
            applicants.some(
              (applicant: Applicant) =>
                applicant.application_status === ApplicantStatus.Submitted
            )
          ) {
            const allRefunded = applicants.every(
              (applicant: Applicant) =>
                applicant.application_fee_status ===
                ApplicationFeeStatus.Refunded
            );
            const somePaid = applicants.some(
              (applicant: Applicant) =>
                applicant.application_fee_status === ApplicationFeeStatus.Paid
            );

            if (allRefunded)
              tooltip =
                "Refund has already been requested for this application";
            else if (somePaid) tooltip = "";
            else
              tooltip =
                "You can request a refund if someone has paid an application fee";
          } else if (status === ApplicationStatus.Canceled)
            tooltip = "You can't request a refund for a canceled application";
          else tooltip = "You can request a refund for submitted applications";

          actions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={CashbackIcon as IconSvgElement}
                  className="text-content-200"
                  size={18}
                />
                Refund application
              </span>
            ),
            disabled: !applicants.some(
              (applicant: Applicant) =>
                applicant.application_fee_status ===
                  ApplicationFeeStatus.Paid &&
                applicant.application_status === ApplicantStatus.Submitted
            ),
            tooltip,
            onClick: () => onRefundClick && onRefundClick(id)
          });
        }

        return (
          <ContextMenuPopover
            placement="bottom-start"
            triggerClassName="-ml-1.5"
            actions={actions}
          />
        );
      }
    });

    // Screening status column (MUST be last with undefined width to satisfy DataGrid requirement)
    cols.push({
      key: "screening_status",
      name: "Screening status",
      minWidth: 100,
      resizable: true,
      sortable: sortableKeys.includes("screening_status"),
      renderCell: ({ row }) => {
        if (row.isGroupHeader) return null;

        return <ScreeningStatusBadge status={row.screening_status} />;
      }
    });

    return cols;
  }, [
    sortedColumnsVisibility,
    hasMultipleCheckedColumns,
    pathname,
    Property,
    Unit,
    selectedRows,
    sortableKeys,
    groupBy
  ]);

  const renderGroupHeader = (
    row: ApplicationWithUIStatus,
    groupBy?: string
  ) => {
    if (!groupBy || !row.groupValue) return null;

    if (groupBy === "status" || groupBy === "ui_status")
      return (
        <div data-attr="group-header">
          <ApplicationStatusBadge
            type="application"
            status={row.groupValue as ApplicationStatus}
          />
        </div>
      );

    if (groupBy === "screening_status")
      return (
        <div data-attr="group-header">
          <ScreeningStatusBadge status={row.groupValue} />
        </div>
      );

    return (
      <div data-attr="group-header">
        <span className="text-content-400 text-sm font-medium">
          {row.groupValue}
        </span>
      </div>
    );
  };

  const [emptyStateTitle, emptyStateText, EmptyStateIcon] = useMemo<
    [string, string, any]
  >(() => {
    if (hasSearchParams)
      return [
        "No results found",
        "We couldn't find any data matching your query",
        Search01Icon
      ];

    if (currentTab.isAll)
      return [
        "Applications will appear here",
        "Invite applicants to create an application",
        FileEmpty02Icon
      ];

    let tabName = activeTab.toLowerCase();
    if (currentTab.isConditionallyApproved) tabName = "conditionally approved";
    else if (currentTab.isOnHold) tabName = "on hold";
    else tabName = capitalize(camelToSentence(tabName));

    const title = `No ${tabName} applications`;

    let prefix: string = activeTab;
    if (currentTab.isConditionallyApproved) prefix = "Conditionally approved";
    else if (currentTab.isOnHold) prefix = "On hold";
    else prefix = capitalize(underscoreToSentence(prefix));

    const text = currentTab.isSubmitted
      ? "Applications awaiting a decision will appear here"
      : `${prefix || ""} applications will appear here`;

    const Icon = FileEmpty02Icon;

    return [title, text, Icon];
  }, [currentTab, activeTab, hasSearchParams]);

  return (
    <DataGridTable
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={200}
      columns={columns}
      perPageRows={perPageRows}
      rows={tableApplications}
      loading={isLoading}
      onRowClick={(row) => {
        if (!row.isGroupHeader) navigate(`/applications/${row.id}`);
      }}
      rowHeight={64}
      headerRowHeight={44}
      sortColumns={sortColumns}
      onSortColumnsChange={handleSortColumnsChange}
      footer={pagination}
      noRowsFallback={
        <NoRowsFallback
          Icon={EmptyStateIcon}
          title={emptyStateTitle}
          text={emptyStateText}
          buttonText={!currentTab.isAll ? undefined : "Invite applicants"}
          buttonAction={
            currentTab.isAll
              ? () => {
                  onInviteClick?.();
                }
              : undefined
          }
        />
      }
    />
  );
};
